import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useCart } from '@/Application/Selectors/cart/useCart';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';

export interface ShippingMethodLineItem {
  sku: string;
  clientPreference?: {
    shippingType?: string;
    shippingSubType?: string;
    shippingMethod?: string;
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    nodeCode?: string;
    timeSlot?: string;
  }[];
}

const shippingPageContext = createContext({
  getSkuHistory: (sku: string) => {
    return { sku } as ShippingMethodLineItem;
  },
  getSkusHistory: (skus: string[]) => {
    return skus.map((sku) => ({ sku }) as ShippingMethodLineItem);
  },
  getSkuHistoryUpdated: async () => {
    return [] as ShippingMethodLineItem[];
  },
  updateSkuHistory: (data: ShippingMethodLineItem) => {
    console.log('updateHistory', data);
  },
  removeSkuFromHistory: (sku: string) => {
    console.log('removeSkuFromHistory', sku);
    return [] as ShippingMethodLineItem[];
  },
  completeSkuHistory: [] as ShippingMethodLineItem[]
});
const ShippingPageProvider = shippingPageContext.Provider;
export const useShippingPageContext = () => useContext(shippingPageContext);

const getDataFromProductList = (productList?: CartLineItem[]) => {
  return (
    productList?.map((product) => {
      return {
        sku: product.variant.sku,
        clientPreference: []
      };
    }) ?? []
  );
};

const ShippingPageContext: FC<PropsWithChildren> = ({ children }) => {
  const { data, refetch } = useCart();
  const [context, setContext] = useState<ShippingMethodLineItem[]>(
    getDataFromProductList(data?.lineItems)
  );

  useEffect(() => {
    if (context.length === 0) {
      setContext(getDataFromProductList(data?.lineItems));
    }
  }, [data, context.length]);

  const getSkuHistoryUpdated = useCallback(async () => {
    const cartData = await refetch();
    return [...getDataFromProductList(cartData?.lineItems)];
  }, [refetch]);

  const getSkuHistory = useCallback(
    (sku: string) => {
      const skuData = context.find((item) => item.sku === sku);
      if (skuData) {
        return skuData;
      }

      return {
        sku: sku,
        clientPreference: []
      };
    },
    [context]
  );

  const getSkusHistory = useCallback(
    (skus: string[]) => {
      return skus.map((sku) => getSkuHistory(sku));
    },
    [getSkuHistory]
  );

  const removeSkuFromHistory = useCallback(
    (sku: string) => {
      const newContext = context.filter((item) => item.sku !== sku);
      setContext(newContext);
      return newContext;
    },
    [setContext, context]
  );

  const updateSkuHistory = useCallback(
    (data: ShippingMethodLineItem) => {
      setContext((prevContext) => {
        const otherSkus = prevContext.filter((item) => item.sku !== data.sku);

        return [...otherSkus, data];
      });
    },
    [setContext]
  );

  return (
    <ShippingPageProvider
      value={{
        getSkuHistory,
        getSkusHistory,
        updateSkuHistory,
        removeSkuFromHistory,
        getSkuHistoryUpdated,
        completeSkuHistory: context
      }}
    >
      {children}
    </ShippingPageProvider>
  );
};

export default ShippingPageContext;
